import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import type { ParsedEntry, ParseErrorRow, ParseSummary } from "./useSiteImport";
import { SummaryStat } from "./SummaryStat";

interface SiteImportResultsProps {
  entries: ParsedEntry[];
  parseErrors: ParseErrorRow[];
  summary: ParseSummary;
  seller: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function SiteImportResults(props: SiteImportResultsProps) {
  const { entries, parseErrors, summary, seller, onConfirm, onCancel } = props;
  const nonDuplicateCount = summary.totalCount - summary.duplicateCount;

  return (
    <Box>
      <Box display="flex" alignItems="baseline" gap={2} flexWrap="wrap" mb={1}>
        <Typography variant="h5">Результаты парсинга</Typography>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Селлер:
          </Typography>
          <Chip label={seller} color="primary" size="small" />
          <Typography variant="caption" color="text.secondary">
            будет проставлен на каждый сохранённый доступ
          </Typography>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <SummaryStat label="Всего строк" value={summary.totalCount} />
          <SummaryStat
            label="Дубликаты доступа в базе (login + password)"
            value={summary.duplicateCount}
            color="warning"
          />
          <SummaryStat
            label="URL уже в базе"
            value={summary.duplicateUrlInDbCount}
            color="info"
          />
          <SummaryStat
            label="Дубли URL внутри CSV"
            value={summary.duplicateUrlInBatchCount}
            color="info"
          />
          <SummaryStat
            label="Ошибки парсинга"
            value={summary.parseErrorCount}
            color="error"
          />
        </Stack>
        {summary.duplicateCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Дубликаты доступа в базе будут пропущены при сохранении ({nonDuplicateCount} {" "}
            {nonDuplicateCount === 1 ? "запись будет добавлена" : "записей будет добавлено"}).
            Дубли URL — только маркер: новые credentials к ним добавятся.
          </Alert>
        )}
      </Paper>

      {parseErrors.length > 0 && (
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Невалидные строки CSV
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, overflowX: "auto" }}>
            <Table size="small" stickyHeader sx={{ "& thead th": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell width={100}>Номер строки</TableCell>
                  <TableCell>Исходный текст</TableCell>
                  <TableCell width={180}>Код ошибки</TableCell>
                  <TableCell>Описание</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parseErrors.map((err, index) => (
                  <TableRow key={index}>
                    <TableCell className="tabular-nums">{err.lineNumber}</TableCell>
                    <TableCell>
                      <code>{err.raw}</code>
                    </TableCell>
                    <TableCell>
                      <Chip label={err.errorCode} size="small" color="error" variant="outlined" />
                    </TableCell>
                    <TableCell>{err.errorMessage}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {entries.length > 0 && (
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Распарсенные записи
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500, overflowX: "auto" }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 1100,
                "& thead th": { whiteSpace: "nowrap" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 240 }}>URL</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Логин</TableCell>
                  <TableCell>Регион</TableCell>
                  <TableCell align="right">DR</TableCell>
                  <TableCell align="right">Organic KW</TableCell>
                  <TableCell align="right">Возраст</TableCell>
                  <TableCell align="right">BlogId</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>Маркеры</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ wordBreak: "break-all" }}>{entry.url}</TableCell>
                    <TableCell sx={{ wordBreak: "break-all" }}>{entry.login}</TableCell>
                    <TableCell>{entry.region}</TableCell>
                    <TableCell align="right">{entry.dr ?? "—"}</TableCell>
                    <TableCell align="right">{entry.organicKeywords ?? "—"}</TableCell>
                    <TableCell align="right">{entry.age ?? "—"}</TableCell>
                    <TableCell align="right">{entry.blogId ?? "—"}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {entry.isDuplicate && (
                          <Chip
                            label="Дубль доступа в базе"
                            size="small"
                            color="warning"
                            title="Login + Password уже встречались (в этом CSV или в базе) — будет пропущено при сохранении"
                          />
                        )}
                        {entry.isDuplicateUrlInBatch && (
                          <Chip
                            label="URL дубль в CSV"
                            size="small"
                            color="info"
                            variant="outlined"
                            title="Этот URL встречается в CSV более одного раза"
                          />
                        )}
                        {entry.isDuplicateUrlInDb && (
                          <Chip
                            label="URL уже в базе"
                            size="small"
                            color="info"
                            title="Сайт с таким URL уже есть в базе"
                          />
                        )}
                        {!entry.isDuplicate &&
                          !entry.isDuplicateUrlInBatch &&
                          !entry.isDuplicateUrlInDb && (
                            <Chip label="Новая запись" size="small" color="success" variant="outlined" />
                          )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box display="flex" gap={1}>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={nonDuplicateCount === 0}
        >
          Подтвердить импорт ({nonDuplicateCount})
        </Button>
        <Button variant="outlined" onClick={onCancel}>
          Отмена
        </Button>
      </Box>
    </Box>
  );
}
